import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useHistory, MemoryRouter, Route } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import MessagesList from "../../components/MessagesList";
import MessageInput from "../../components/MessageInput";
import TransferTicketModal from "../../components/TransferTicketModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { getTheme } from "../CrmTickets/tickets/theme";
import {
  CH,
  PRIO,
  TAG_KEYS,
  CH_KEYS,
  PRIO_KEYS,
  VARIANT_FONT,
  attendantColor,
  initials,
} from "../CrmKanban/kanban/tokens";

// -------------------------------------------------------------------------
// Fontes (mesma URL usada em CrmTickets/index.js) — injetadas uma única vez.
const ensureFonts = () => {
  const id = "crm-lead-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Nunito:wght@400;700;800;900&family=Outfit:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap";
  document.head.appendChild(link);
};

// Variante lida do localStorage do Kanban (default funnel).
const loadVariant = () => {
  try {
    const raw = localStorage.getItem("crmKanbanSettings");
    if (!raw) return "funnel";
    const v = JSON.parse(raw)?.variant;
    return v || "funnel";
  } catch {
    return "funnel";
  }
};

// -------------------------------------------------------------------------
// Avatar (foto do contato com fallback p/ iniciais coloridas).
const Avatar = ({ url, name, size, radius, fontSize = 13 }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const base = {
    width: size,
    height: size,
    borderRadius: radius,
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={name || ""}
        onError={() => setBroken(true)}
        style={{ ...base, objectFit: "cover", background: "#e9edf2" }}
      />
    );
  }
  return (
    <span
      style={{
        ...base,
        background: attendantColor(name),
        color: "#fff",
        fontSize,
        fontWeight: 800,
      }}
    >
      {initials(name)}
    </span>
  );
};

// -------------------------------------------------------------------------
// Estilos base de input/select por variante.
const inputStyle = (V) => ({
  background: V.inputBg,
  border: V.inputBorder,
  borderRadius: V.inputRadius,
  padding: "8px 10px",
  fontSize: 12.5,
  fontFamily: "inherit",
  fontWeight: 400,
  color: V.textStrong,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
});

const selectStyle = (V) => ({
  ...inputStyle(V),
  fontWeight: 700,
  fontSize: 12,
  cursor: "pointer",
});

const FieldLabel = ({ V, children }) => (
  <span
    style={{
      fontSize: 10.5,
      fontWeight: 800,
      color: V.sublabel,
      marginBottom: 3,
      display: "block",
    }}
  >
    {children}
  </span>
);

const TextField = ({
  V,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}) => (
  <label style={{ display: "block", minWidth: 0 }}>
    <FieldLabel V={V}>{label}</FieldLabel>
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle(V)}
    />
  </label>
);

const SelectField = ({ V, label, value, options, onChange }) => (
  <label style={{ display: "block", minWidth: 0 }}>
    <FieldLabel V={V}>{label}</FieldLabel>
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={selectStyle(V)}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

const SectionTitle = ({ V, children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: ".06em",
      color: V.labelMuted,
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

// -------------------------------------------------------------------------
// Composer real do WhatsApp (MessageInput lê ticketId de useParams()).
const ComposerWithRouter = ({ ticketId, ticketStatus }) => (
  <MemoryRouter
    initialEntries={[`/${ticketId}`]}
    key={ticketId}
    getUserConfirmation={() => {}}
  >
    <Route path="/:ticketId">
      <MessageInput ticketStatus={ticketStatus} />
    </Route>
  </MemoryRouter>
);

// -------------------------------------------------------------------------
// Modal de mesclagem de leads.
const MergeModal = ({ V, currentLeadId, onClose, onMerged }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/crm/leads", {
          params: { searchParam: term },
        });
        if (alive) {
          const list = data?.leads || [];
          setResults(
            list.filter((l) => String(l.id) !== String(currentLeadId)),
          );
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, currentLeadId]);

  const doMerge = async (target) => {
    try {
      await api.post(`/crm/leads/${currentLeadId}/merge`, {
        targetLeadId: target.id,
      });
      onMerged(target.id);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,20,30,.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420,
          maxWidth: "90vw",
          maxHeight: "80vh",
          background: "#fff",
          borderRadius: V.qrModalRadius || 14,
          border: V.panelBorder,
          boxShadow: "0 18px 50px rgba(20,20,40,.24)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 18px 12px",
            borderBottom: V.divider,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: V.textStrong,
              flex: 1,
            }}
          >
            Mesclar lead
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: V.labelMuted,
              fontSize: 20,
              cursor: "pointer",
              fontFamily: "inherit",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "14px 18px", borderBottom: V.divider }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar lead por nome ou número…"
            style={inputStyle(V)}
          />
          <div
            style={{
              fontSize: 10.5,
              color: V.labelMuted,
              marginTop: 6,
            }}
          >
            Este lead será mesclado no lead escolhido.
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: "8px 10px", flex: 1 }}>
          {loading && (
            <div
              style={{
                fontSize: 12,
                color: V.labelMuted,
                padding: "10px 8px",
              }}
            >
              Buscando…
            </div>
          )}
          {!loading && q.trim() && results.length === 0 && (
            <div
              style={{
                fontSize: 12,
                color: V.labelMuted,
                padding: "10px 8px",
              }}
            >
              Nenhum lead encontrado.
            </div>
          )}
          {results.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => doMerge(l)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                textAlign: "left",
                background: "#fff",
                border: V.panelBorder,
                borderRadius: V.btnRadius === 999 ? 12 : V.btnRadius,
                padding: "9px 11px",
                marginBottom: 6,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <Avatar
                url={l.contact?.profilePicUrl}
                name={l.name || l.contact?.name}
                size={30}
                radius={V.avatarRadius === "50%" ? "50%" : 9}
                fontSize={11}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: V.textStrong,
                  }}
                >
                  {l.name || l.contact?.name || "Sem nome"}
                </div>
                <div style={{ fontSize: 11, color: V.phoneText }}>
                  {l.number || l.contact?.number || ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
const CrmLead = () => {
  const { leadId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [variant] = useState(loadVariant);
  const V = getTheme(variant);
  const font = VARIANT_FONT[variant] || VARIANT_FONT.funnel;
  const funnel = V.key === "b";

  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null); // rascunho editável
  const [stages, setStages] = useState([]);
  const [connections, setConnections] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [saving, setSaving] = useState(false);

  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");

  const [mergeOpen, setMergeOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [confirmAbandonOpen, setConfirmAbandonOpen] = useState(false);
  const [formTab, setFormTab] = useState("contato");

  // Link de assinatura do pedido (ERP). O botão só aparece se available: true.
  const [salePortal, setSalePortal] = useState(null);
  const ticketIdForPortal = lead?.ticketId;

  const fetchSalePortal = useCallback(async () => {
    if (!ticketIdForPortal) {
      setSalePortal(null);
      return;
    }
    try {
      const { data } = await api.get(
        `/crm/tickets/${ticketIdForPortal}/sale-portal-link`,
      );
      setSalePortal(data);
    } catch (err) {
      setSalePortal({ available: false });
    }
  }, [ticketIdForPortal]);

  useEffect(() => {
    fetchSalePortal();
  }, [fetchSalePortal]);

  useEffect(() => {
    ensureFonts();
  }, []);

  // Carrega o lead.
  const loadLead = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/leads/${leadId}`);
      setLead(data);
      setForm({
        name: data.name || "",
        number: data.number || "",
        email: data.email || "",
        value: data.value != null ? String(data.value) : "",
        empresa: data.empresa || "",
        cidade: data.cidade || "",
        stageId: data.stageId || "",
        tag: data.tag || "",
        channel: data.channel || "",
        priority: data.priority || "",
        whatsappId: data.whatsappId || "",
        userId: data.userId || "",
        notes: data.notes || "",
      });
    } catch (err) {
      toastError(err);
    }
  }, [leadId]);

  useEffect(() => {
    loadLead();
  }, [loadLead]);

  // Listas auxiliares.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/crm/stages");
        setStages(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/whatsapp");
        setConnections(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
      try {
        const { data } = await api.get("/users/");
        setAttendants(data?.users || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Notas do lead.
  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/notes`);
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, [leadId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        number: form.number,
        email: form.email,
        value: Number(String(form.value).replace(/[^0-9.]/g, "")) || 0,
        empresa: form.empresa,
        cidade: form.cidade,
        stageId: form.stageId ? Number(form.stageId) : null,
        tag: form.tag || null,
        channel: form.channel || null,
        priority: form.priority || null,
        whatsappId: form.whatsappId ? Number(form.whatsappId) : null,
        userId: form.userId ? Number(form.userId) : null,
        notes: form.notes,
      };
      const { data } = await api.put(`/crm/leads/${leadId}`, payload);
      if (data) {
        setLead((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    const body = noteDraft.trim();
    if (!body) return;
    setNoteDraft("");
    try {
      await api.post(`/crm/leads/${leadId}/notes`, { body });
      loadNotes();
    } catch (err) {
      toastError(err);
    }
  };

  const removeNote = async (noteId) => {
    try {
      await api.delete(`/crm/leads/${leadId}/notes/${noteId}`);
      loadNotes();
    } catch (err) {
      toastError(err);
    }
  };

  const startConversation = async () => {
    try {
      const { data } = await api.post("/tickets", {
        contactId: lead.contactId,
        status: "open",
        userId: user?.id,
      });
      if (data?.id) {
        setLead((prev) => ({ ...prev, ticketId: data.id }));
      }
    } catch (err) {
      toastError(err);
    }
  };

  // ---- Ações do ticket (status-aware). Recarrega o lead após cada PUT. ----
  const returnTicket = async () => {
    if (!lead.ticketId) return;
    try {
      await api.put(`/tickets/${lead.ticketId}`, {
        status: "pending",
        userId: null,
      });
      loadLead();
    } catch (err) {
      toastError(err);
    }
  };

  const takeTicket = async () => {
    if (!lead.ticketId) return;
    try {
      await api.put(`/tickets/${lead.ticketId}`, {
        status: "open",
        userId: user?.id || null,
      });
      loadLead();
    } catch (err) {
      toastError(err);
    }
  };

  // Abrir venda: cria o pedido rascunho no ERP; o ticket segue aberto.
  const openSale = async () => {
    if (!lead.ticketId) return;
    try {
      const { data } = await api.post(
        `/crm/tickets/${lead.ticketId}/open-sale`,
      );
      if (data?.alreadyOpen) {
        toast.warn(`Venda ${data.order_number} já está aberta para este lead`);
      } else {
        toast.success("Venda aberta no ERP");
      }
      loadLead();
      fetchSalePortal();
    } catch (err) {
      toastError(err);
    }
  };

  // Abandonar: fecha o ticket sem venda (mesmo payload do TicketActionButtons).
  const abandonTicket = async () => {
    if (!lead.ticketId) return;
    try {
      await api.put(`/tickets/${lead.ticketId}`, {
        status: "closed",
        userId: user?.id || null,
      });
      toast.success("Ticket abandonado");
      loadLead();
    } catch (err) {
      toastError(err);
    }
  };

  // Envia o link público de assinatura como mensagem no ticket (mesmo endpoint
  // e payload do MessageInput). O botão continua visível até o cliente assinar.
  const sendSignatureLink = async () => {
    if (!lead.ticketId || !salePortal?.available) return;
    try {
      // Busca o link na hora do envio: o token pode ter expirado desde o
      // carregamento da tela (o ERP renova e devolve o link novo).
      const { data: fresh } = await api.get(
        `/crm/tickets/${lead.ticketId}/sale-portal-link`,
      );
      setSalePortal(fresh);
      if (!fresh?.available) {
        toast.warn("Link indisponível — verifique a venda no ERP");
        return;
      }
      await api.post(`/messages/${lead.ticketId}`, {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body:
          fresh.message ||
          `Olá! Segue o link para revisão e assinatura do pedido ${fresh.order_number}: ${fresh.url}`,
        quotedMsg: null,
      });
      toast.success("Link de assinatura enviado");
    } catch (err) {
      toastError(err);
    }
  };

  const shortTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---- loading ----
  if (!lead || !form) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: V.pageBg,
          fontFamily: font,
          color: V.labelMuted,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        Carregando lead…
      </div>
    );
  }

  const stage = lead.stage || {};
  const ticketStatus = lead.ticketId ? lead.ticket?.status || "open" : null;
  const attendantName = lead.user?.name || "";
  const contactName =
    lead.contact?.name || lead.name || lead.contact?.number || "Sem nome";
  const contactNumber = lead.contact?.number || lead.number || "";

  const stageOptions = [
    { value: "", label: "Sem estágio" },
    ...stages.map((s) => ({ value: s.id, label: s.name })),
  ];
  const tagOptions = [
    { value: "", label: "—" },
    ...TAG_KEYS.map((t) => ({ value: t, label: t })),
  ];
  const channelOptions = [
    { value: "", label: "—" },
    ...CH_KEYS.map((c) => ({ value: c, label: CH[c][0] })),
  ];
  const prioOptions = [
    { value: "", label: "—" },
    ...PRIO_KEYS.map((p) => ({ value: p, label: PRIO[p][0] })),
  ];
  const connOptions = [
    { value: "", label: "Sem conexão" },
    ...connections.map((c) => ({ value: c.id, label: c.name })),
  ];
  const attendantOptions = [
    { value: "", label: "Sem atendente" },
    ...attendants.map((a) => ({ value: a.id, label: a.name })),
  ];

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: V.pageBg,
        fontFamily: font,
        color: V.textStrong,
        overflow: "hidden",
      }}
    >
      {/* -------- Header -------- */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 18px",
          background: V.headerBg,
          borderBottom: V.headerBorder,
          flex: "none",
        }}
      >
        <button
          type="button"
          onClick={() => history.push("/crm-kanban")}
          title="Voltar ao Kanban"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            background: "#fff",
            border: V.panelBorder,
            borderRadius: funnel ? 8 : 999,
            color: V.sublabel,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: V.textStrong,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {contactName}
        </div>

        {stage.name && (
          <span
            style={{
              background: (stage.color || "#94A3B8") + "22",
              color: stage.color || "#64748B",
              fontSize: 11,
              fontWeight: 800,
              padding: "3px 11px",
              borderRadius: V.pillRadius,
              whiteSpace: "nowrap",
            }}
          >
            {stage.name}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {attendantName ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: attendantColor(attendantName),
                color: "#fff",
                fontSize: 10.5,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {initials(attendantName)}
            </span>
            <span
              style={{ fontSize: 12.5, fontWeight: 700, color: V.sublabel }}
            >
              {attendantName}
            </span>
          </span>
        ) : (
          <span
            style={{
              background: "#FFF3DC",
              color: "#B7791F",
              fontSize: 11,
              fontWeight: 800,
              padding: "4px 11px",
              borderRadius: V.pillRadius,
            }}
          >
            Sem atendente
          </span>
        )}
      </div>

      {/* -------- Corpo: 2 colunas -------- */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* ---- Esquerda: form ---- */}
        <div
          style={{
            width: 380,
            flex: "none",
            background: V.panelBg,
            borderRight: V.panelBorder,
            overflowY: "auto",
            padding: "18px 18px 28px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            boxSizing: "border-box",
          }}
        >
          {/* Abas do form: Contato / Negócio / Notas */}
          <div style={{ display: "flex", borderBottom: V.divider, gap: 0 }}>
            {[
              ["contato", "Contato"],
              ["negocio", "Negócio"],
              ["notas", "Notas"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFormTab(key)}
                style={{
                  flex: 1,
                  background: "none",
                  border: "none",
                  borderBottom: `2.5px solid ${
                    formTab === key ? V.prim : "transparent"
                  }`,
                  color: formTab === key ? V.prim : V.mut || V.labelMuted,
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: ".02em",
                  padding: "9px 0 8px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Aba: Contato */}
          {formTab === "contato" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SectionTitle V={V}>Dados do contato</SectionTitle>
              <TextField
                V={V}
                label="Nome"
                value={form.name}
                onChange={(v) => setField("name", v)}
                placeholder="Nome do lead"
              />
              <TextField
                V={V}
                label="Número"
                value={form.number}
                onChange={(v) => setField("number", v)}
                placeholder="55…"
              />
              <TextField
                V={V}
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(v) => setField("email", v)}
                placeholder="email@exemplo.com"
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <TextField
                  V={V}
                  label="Empresa"
                  value={form.empresa}
                  onChange={(v) => setField("empresa", v)}
                  placeholder="Empresa"
                />
                <TextField
                  V={V}
                  label="Cidade"
                  value={form.cidade}
                  onChange={(v) => setField("cidade", v)}
                  placeholder="Cidade"
                />
              </div>
            </div>
          )}

          {/* Aba: Negócio */}
          {formTab === "negocio" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SectionTitle V={V}>Negócio</SectionTitle>
              <SelectField
                V={V}
                label="Estágio"
                value={form.stageId}
                options={stageOptions}
                onChange={(v) => setField("stageId", v)}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <SelectField
                  V={V}
                  label="Tag"
                  value={form.tag}
                  options={tagOptions}
                  onChange={(v) => setField("tag", v)}
                />
                <SelectField
                  V={V}
                  label="Canal"
                  value={form.channel}
                  options={channelOptions}
                  onChange={(v) => setField("channel", v)}
                />
                <SelectField
                  V={V}
                  label="Prioridade"
                  value={form.priority}
                  options={prioOptions}
                  onChange={(v) => setField("priority", v)}
                />
                <TextField
                  V={V}
                  label="Valor (R$)"
                  type="number"
                  value={form.value}
                  onChange={(v) => setField("value", v)}
                  placeholder="0"
                />
              </div>
              <SelectField
                V={V}
                label="Conexão"
                value={form.whatsappId}
                options={connOptions}
                onChange={(v) => setField("whatsappId", v)}
              />
              <SelectField
                V={V}
                label="Atendente"
                value={form.userId}
                options={attendantOptions}
                onChange={(v) => setField("userId", v)}
              />
            </div>
          )}

          {/* Botão Salvar (aparece só em Contato e Negócio) */}
          {(formTab === "contato" || formTab === "negocio") && (
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{
                background: V.prim,
                border: "none",
                color: "#fff",
                borderRadius: V.btnRadius,
                padding: "11px 16px",
                fontWeight: 800,
                fontSize: 13.5,
                fontFamily: "inherit",
                cursor: saving ? "default" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Salvando…" : "Salvar"}
            </button>
          )}

          {/* Aba: Notas (timeline) */}
          {formTab === "notas" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <SectionTitle V={V}>Notas</SectionTitle>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addNote();
                  }}
                  placeholder="Adicionar nota…"
                  style={{ ...inputStyle(V), flex: 1, minWidth: 0 }}
                />
                <button
                  type="button"
                  onClick={addNote}
                  style={{
                    background: V.prim,
                    border: "none",
                    color: "#fff",
                    borderRadius: V.btnRadius === 999 ? 10 : V.btnRadius,
                    padding: "8px 14px",
                    fontWeight: 900,
                    fontSize: 15,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    flex: "none",
                  }}
                >
                  +
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {notes.length === 0 && (
                  <div
                    style={{
                      fontSize: 11.5,
                      color: V.labelMuted,
                      padding: "4px 2px",
                    }}
                  >
                    Nenhuma nota ainda.
                  </div>
                )}
                {notes.map((n) => (
                  <div
                    key={n.id}
                    style={{
                      background: V.noteBg,
                      border: V.noteBorder,
                      borderRadius: V.fieldRadius,
                      padding: "8px 10px",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: V.noteText,
                        lineHeight: 1.45,
                        paddingRight: 16,
                      }}
                    >
                      {n.body}
                    </div>
                    <div
                      style={{
                        fontSize: 9.5,
                        color: V.noteTime,
                        textAlign: "right",
                        marginTop: 3,
                      }}
                    >
                      {n.user?.name ? `${n.user.name} · ` : ""}
                      {shortTime(n.createdAt)}
                    </div>
                    <button
                      type="button"
                      title="Excluir nota"
                      onClick={() => removeNote(n.id)}
                      style={{
                        position: "absolute",
                        top: 5,
                        right: 7,
                        background: "none",
                        border: "none",
                        color: V.noteTime,
                        fontSize: 13,
                        fontWeight: 800,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- Direita: chat ---- */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            background: V.chatBg,
          }}
        >
          {lead.ticketId ? (
            <ReplyMessageProvider>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                {/* Header estilo WhatsApp */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    padding: "10px 16px",
                    background: V.headerBg,
                    borderBottom: V.headerBorder,
                    flex: "none",
                  }}
                >
                  <Avatar
                    url={lead.contact?.profilePicUrl}
                    name={contactName}
                    size={38}
                    radius={funnel ? 10 : "50%"}
                    fontSize={13}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: V.textStrong,
                      }}
                    >
                      {contactName}
                    </div>
                    <div style={{ fontSize: 11.5, color: V.phoneText }}>
                      {contactNumber}
                    </div>
                  </div>
                </div>

                {/* Mensagens reais */}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    background: V.chatBg,
                    overflow: "hidden",
                  }}
                >
                  <MessagesList ticketId={lead.ticketId} isGroup={false} />
                </div>

                {/* Barra de ações rápidas (status-aware) */}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "7px 14px",
                    background: V.quickBg,
                    borderTop: V.quickBorder,
                    flex: "none",
                  }}
                >
                  {ticketStatus !== "closed" && (
                    <button
                      type="button"
                      onClick={() => setTransferOpen(true)}
                      style={{
                        background: "#fff",
                        border: funnel
                          ? "1px solid #E2E7EE"
                          : "1px solid #E8DFCE",
                        color: V.sublabel,
                        borderRadius: funnel ? 7 : 999,
                        padding: "5px 12px",
                        fontWeight: 800,
                        fontSize: 11,
                        fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      Transferir
                    </button>
                  )}
                  {ticketStatus === "open" && (
                    <button
                      type="button"
                      onClick={returnTicket}
                      title="Retornar o ticket para a fila"
                      style={{
                        background: "#fff",
                        border: funnel
                          ? "1px solid #E2E7EE"
                          : "1px solid #E8DFCE",
                        color: V.sublabel,
                        borderRadius: funnel ? 7 : 999,
                        padding: "5px 12px",
                        fontWeight: 800,
                        fontSize: 11,
                        fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      Retornar
                    </button>
                  )}
                  <span style={{ flex: 1 }} />
                  {salePortal?.available && (
                    <button
                      type="button"
                      onClick={sendSignatureLink}
                      title="Enviar o link público de assinatura do pedido no chat"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        background: "#fff",
                        border: funnel
                          ? "1px solid #E2E7EE"
                          : "1px solid #E8DFCE",
                        color: V.sublabel,
                        borderRadius: funnel ? 7 : 999,
                        padding: "5px 12px",
                        fontWeight: 800,
                        fontSize: 11,
                        fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                      </svg>
                      Enviar link de assinatura
                    </button>
                  )}
                  {(() => {
                    const primary = {
                      background: "#1F9D57",
                      border: "none",
                      color: "#fff",
                      borderRadius: funnel ? 7 : 999,
                      padding: "5px 14px",
                      fontWeight: 900,
                      fontSize: 11,
                      fontFamily: "inherit",
                      cursor: "pointer",
                    };
                    if (ticketStatus === "pending") {
                      return (
                        <button
                          type="button"
                          onClick={takeTicket}
                          title="Aceitar e começar o atendimento"
                          style={primary}
                        >
                          Aceitar
                        </button>
                      );
                    }
                    if (ticketStatus === "closed") {
                      return (
                        <button
                          type="button"
                          onClick={takeTicket}
                          title="Reabrir o ticket"
                          style={primary}
                        >
                          Reabrir
                        </button>
                      );
                    }
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setConfirmAbandonOpen(true)}
                          title="Encerrar o atendimento sem venda"
                          style={{ ...primary, background: "#d32f2f" }}
                        >
                          Abandonar Ticket
                        </button>
                        <button
                          type="button"
                          onClick={openSale}
                          title="Abrir a venda no ERP (o atendimento segue aberto)"
                          style={primary}
                        >
                          Abrir venda
                        </button>
                      </>
                    );
                  })()}
                </div>

                {/* Composer real */}
                <ComposerWithRouter
                  ticketId={lead.ticketId}
                  ticketStatus={ticketStatus}
                />
              </div>
            </ReplyMessageProvider>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                padding: 24,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: V.textStrong,
                }}
              >
                Sem conversa ainda
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: V.labelMuted,
                  textAlign: "center",
                  maxWidth: 320,
                }}
              >
                Inicie uma conversa com este contato ou mescle o lead a outro já
                existente.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={startConversation}
                  disabled={!lead.contactId}
                  style={{
                    background: V.prim,
                    border: "none",
                    color: "#fff",
                    borderRadius: V.btnRadius,
                    padding: "10px 18px",
                    fontWeight: 800,
                    fontSize: 13,
                    fontFamily: "inherit",
                    cursor: lead.contactId ? "pointer" : "default",
                    opacity: lead.contactId ? 1 : 0.5,
                  }}
                >
                  Iniciar conversa
                </button>
                <button
                  type="button"
                  onClick={() => setMergeOpen(true)}
                  style={{
                    background: "#fff",
                    border: V.panelBorder,
                    color: V.sublabel,
                    borderRadius: V.btnRadius,
                    padding: "10px 18px",
                    fontWeight: 800,
                    fontSize: 13,
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  Mesclar lead
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        title="Abandonar Ticket"
        open={confirmAbandonOpen}
        onClose={setConfirmAbandonOpen}
        onConfirm={abandonTicket}
      >
        Abandonar este atendimento? O ticket será fechado.
      </ConfirmationModal>

      {lead.ticketId && (
        <TransferTicketModal
          modalOpen={transferOpen}
          onClose={() => setTransferOpen(false)}
          ticketid={lead.ticketId}
          ticketWhatsappId={lead.whatsappId}
        />
      )}

      {mergeOpen && (
        <MergeModal
          V={V}
          currentLeadId={leadId}
          onClose={() => setMergeOpen(false)}
          onMerged={(targetId) => {
            setMergeOpen(false);
            history.push("/crm-lead/" + targetId);
          }}
        />
      )}
    </div>
  );
};

export default CrmLead;
